/**
 * Modern Input component with refined interactions
 */

import { forwardRef, ReactNode, useState } from 'react';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
  rightIcon?: ReactNode;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      hint,
      id,
      icon,
      rightIcon,
      type,
      showPasswordToggle,
      disabled,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name;
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword && showPassword ? 'text' : type;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'block text-sm font-medium mb-2 transition-colors',
              error
                ? 'text-red-600 dark:text-red-400'
                : 'text-neutral-700 dark:text-neutral-300'
            )}
          >
            {label}
          </label>
        )}
        <div className="relative group">
          {icon && (
            <div
              className={cn(
                'absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none transition-colors',
                error
                  ? 'text-red-500 dark:text-red-400'
                  : 'text-neutral-400 group-focus-within:text-primary-500 dark:text-neutral-500 dark:group-focus-within:text-primary-400'
              )}
            >
              {icon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            disabled={disabled}
            className={cn(
              // Base styles
              'w-full py-3 rounded-xl transition-all duration-200',
              'bg-white dark:bg-neutral-900',
              'text-neutral-900 dark:text-neutral-100',
              'placeholder:text-neutral-400 dark:placeholder:text-neutral-500',
              // Border
              'border',
              error
                ? 'border-red-500 dark:border-red-400'
                : 'border-neutral-300 dark:border-neutral-700',
              // Hover
              !disabled && !error && 'hover:border-neutral-400 dark:hover:border-neutral-600',
              // Focus
              'focus:outline-none focus:ring-2',
              error
                ? 'focus:ring-red-500/20 focus:border-red-500 dark:focus:border-red-400'
                : 'focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400',
              // Padding
              icon ? 'pl-11' : 'px-4',
              (rightIcon || (isPassword && showPasswordToggle)) ? 'pr-11' : 'pr-4',
              // Disabled
              disabled && 'opacity-50 cursor-not-allowed bg-neutral-50 dark:bg-neutral-800',
              className
            )}
            {...props}
          />
          {/* Right icon or password toggle */}
          {(rightIcon || (isPassword && showPasswordToggle)) && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2">
              {isPassword && showPasswordToggle ? (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={cn(
                    'p-1 rounded-lg transition-colors',
                    'text-neutral-400 hover:text-neutral-600',
                    'dark:text-neutral-500 dark:hover:text-neutral-300',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500'
                  )}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              ) : (
                <span className="text-neutral-400 dark:text-neutral-500">
                  {rightIcon}
                </span>
              )}
            </div>
          )}
          {/* Error icon */}
          {error && !rightIcon && !(isPassword && showPasswordToggle) && (
            <div className="absolute right-3.5 top-1/2 -translate-y-1/2 text-red-500 dark:text-red-400">
              <AlertCircle className="w-5 h-5" />
            </div>
          )}
        </div>
        {/* Error message */}
        {error && (
          <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </p>
        )}
        {/* Hint text */}
        {hint && !error && (
          <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
